import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from './supabaseClient';

const DEFAULTS = {
  story_p1: "It was a simple gift from my mother — just a standard houseplant. But like a lot of people, I quickly became obsessed with trying to grow it bigger, healthier, and better than what I was seeing online. Once I found plant TikTok, that obsession only grew.",
  story_p2: "What started as casual houseplant collecting turned into something much bigger when I discovered my first local nursery carrying rare plants. That's when I began diving into the world of uncommon aroids, collector plants, and the fast-moving plant market.",
  story_p3: "As I started buying, selling, and trading plants locally through Facebook Marketplace, I ran into a problem every collector faces:",
  story_quote: "How do you know what a plant is actually worth?",
  story_p4: "Pricing plants isn't easy. Big box stores like Home Depot and Lowe's can give you a baseline for common plants, but when it comes to rare or collector plants, there's no true database. Prices vary wildly depending on rarity, size, variegation, and market trends.",
  story_p5: "To find answers, I found myself searching everywhere — eBay, Palmstreet, private sellers, plant groups — trying to piece together real values. Over time, I started building spreadsheets to track sold prices, monitor trends, and better understand the market.",
  story_p6: "That process felt familiar.",
  story_p7: "For over 12 years, I worked in the pawn industry, where understanding value means everything. Finding the highest and lowest market prices, spotting patterns, and tracking trends became second nature to me.",
  story_p8: "I realized plants needed the same thing.",
  why_text: "To build a reliable, community-driven platform where collectors, buyers, and sellers can track real plant values, monitor trends, and make informed decisions.",
  vision_text: "My vision for True Plant Value is bigger than just pricing. I want it to become a multi-layered platform where plant enthusiasts can track their collections, store personal pricing history, monitor market changes, and build a stronger understanding of the plants they love.",
  welcome_text: "Whether you're here to buy, sell, collect, or just learn — we're building this together.",
};

const About = () => {
  const navigate = useNavigate();
  const isMobile = window.innerWidth <= 768;
  const [aboutPhoto, setAboutPhoto] = useState('');
  const [text, setText] = useState(DEFAULTS);

  useEffect(() => {
    const fetch = async () => {
      const { data: photoData } = await supabase.from('site_settings').select('value').eq('key', 'about_photo').single();
      if (photoData && photoData.value) setAboutPhoto(photoData.value);

      const { data: textData } = await supabase.from('site_settings').select('*').like('key', 'about_%');
      if (textData) {
        const updates = {};
        textData.forEach(row => {
          const field = row.key.replace('about_', '');
          if (field !== 'photo' && row.value) updates[field] = row.value;
        });
        setText(prev => ({ ...prev, ...updates }));
      }
    };
    fetch();
  }, []);

  return (
    <div style={{ paddingBottom: '80px' }}>

      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3d20 0%, #2c4a2e 60%, #4a7c59 100%)',
        padding: 'clamp(48px, 8vw, 80px) 24px',
        textAlign: 'center',
        borderRadius: '0 0 40px 40px',
        marginBottom: '60px',
      }}>
        {aboutPhoto && (
          <img src={aboutPhoto} alt="About True Plant Value" style={{ width: '100%', maxWidth: '600px', maxHeight: '300px', objectFit: 'cover', borderRadius: '20px', marginBottom: '28px', display: 'block', marginLeft: 'auto', marginRight: 'auto', border: '3px solid rgba(245,240,232,0.2)' }} />
        )}
        <div style={{ fontSize: '11px', letterSpacing: '6px', color: '#8fb99a', textTransform: 'uppercase', marginBottom: '16px', fontFamily: 'Lato, sans-serif' }}>🌿 Our Story</div>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '36px' : '56px', color: '#f5f0e8', fontWeight: 700, marginBottom: '16px', lineHeight: 1.1 }}>
          About True Plant Value
        </h1>
        <p style={{ color: 'rgba(245,240,232,0.75)', fontFamily: 'Lato, sans-serif', fontSize: '18px', maxWidth: '520px', margin: '0 auto', lineHeight: 1.7 }}>
          Built by a collector, for collectors.
        </p>
      </div>

      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 24px' }}>

        {/* Story */}
        <div style={{ background: '#fff', borderRadius: '24px', padding: isMobile ? '28px 20px' : '48px', boxShadow: '0 4px 24px rgba(44,74,46,0.08)', border: '1px solid rgba(143,185,154,0.25)', marginBottom: '40px' }}>

          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '28px', color: '#1e3d20', marginBottom: '20px' }}>
            It Started With a Single Monstera.
          </h2>

          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '16px', color: '#4a5e4b', lineHeight: 1.9, marginBottom: '20px' }}>{text.story_p1}</p>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '16px', color: '#4a5e4b', lineHeight: 1.9, marginBottom: '20px' }}>{text.story_p2}</p>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '16px', color: '#4a5e4b', lineHeight: 1.9, marginBottom: '20px' }}>{text.story_p3}</p>

          <div style={{ background: '#f0f7f1', borderLeft: '4px solid #4a7c59', borderRadius: '0 16px 16px 0', padding: '20px 24px', margin: '28px 0' }}>
            <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#1e3d20', margin: 0, lineHeight: 1.5, fontStyle: 'italic' }}>
              "{text.story_quote}"
            </p>
          </div>

          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '16px', color: '#4a5e4b', lineHeight: 1.9, marginBottom: '20px' }}>{text.story_p4}</p>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '16px', color: '#4a5e4b', lineHeight: 1.9, marginBottom: '20px' }}>{text.story_p5}</p>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '16px', color: '#4a5e4b', lineHeight: 1.9, marginBottom: '20px' }}>{text.story_p6}</p>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '16px', color: '#4a5e4b', lineHeight: 1.9, marginBottom: '20px' }}>{text.story_p7}</p>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '16px', color: '#4a5e4b', lineHeight: 1.9, marginBottom: '0' }}>{text.story_p8}</p>
        </div>

        {/* Why */}
        <div style={{ background: 'linear-gradient(135deg, #1e3d20, #2c4a2e)', borderRadius: '24px', padding: isMobile ? '28px 20px' : '48px', marginBottom: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌿</div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '28px', color: '#f5f0e8', marginBottom: '20px' }}>
            That's Why I Created True Plant Value.
          </h2>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '17px', color: '#8fb99a', lineHeight: 1.9, maxWidth: '560px', margin: '0 auto' }}>
            {text.why_text}
          </p>
        </div>

        {/* Vision */}
        <div style={{ background: '#fff', borderRadius: '24px', padding: isMobile ? '28px 20px' : '48px', boxShadow: '0 4px 24px rgba(44,74,46,0.08)', border: '1px solid rgba(143,185,154,0.25)', marginBottom: '40px' }}>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '28px', color: '#1e3d20', marginBottom: '20px' }}>
            But This Is Only the Beginning.
          </h2>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '16px', color: '#4a5e4b', lineHeight: 1.9, marginBottom: '20px' }}>
            {text.vision_text}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginTop: '28px' }}>
            {[
              { icon: '📊', text: 'Track real sold prices from the community' },
              { icon: '📈', text: 'Monitor market trends over time' },
              { icon: '🌱', text: 'Manage your personal plant collection' },
              { icon: '💚', text: 'Make informed buying and selling decisions' },
            ].map(({ icon, text: itemText }) => (
              <div key={itemText} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: '#f0f7f1', borderRadius: '14px' }}>
                <span style={{ fontSize: '24px' }}>{icon}</span>
                <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '14px', color: '#2c4a2e', fontWeight: 600 }}>{itemText}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Welcome */}
        <div style={{ textAlign: 'center', padding: isMobile ? '28px 20px' : '48px', background: '#f0f7f1', borderRadius: '24px', marginBottom: '40px' }}>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '32px', color: '#1e3d20', marginBottom: '16px' }}>
            Welcome. 🌿
          </h2>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '17px', color: '#4a5e4b', lineHeight: 1.9, maxWidth: '480px', margin: '0 auto 28px' }}>
            {text.welcome_text}
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/search')} style={{ background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '24px', padding: '14px 32px', fontSize: '14px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
              Search Plants
            </button>
            <button onClick={() => navigate('/submit')} style={{ background: 'transparent', color: '#2c4a2e', border: '2px solid #2c4a2e', borderRadius: '24px', padding: '14px 32px', fontSize: '14px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
              Submit a Sale
            </button>
          </div>
        </div>

        {/* Contact */}
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '14px', color: '#8b9e8c' }}>
            Questions or feedback? Reach out at{' '}
            <a href="mailto:trueplantvalue@gmail.com" style={{ color: '#4a7c59', fontWeight: 700, textDecoration: 'none' }}>
              trueplantvalue@gmail.com
            </a>
          </p>
        </div>

      </div>
    </div>
  );
};

export default About;